import { styled } from '@linaria/react';
import { useState } from 'react';
import { type CapabilityGroup, CapabilityMode } from '../../../../api/agents-client/agent-client.type';
import { Icon } from '../../../../ui-library/icon/icon';
import { Switch } from '../../../../ui-library/switch/switch';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { IconTile } from '../shared/settings/icon-tile';
import { ExpandChevron, ExpandButton as SharedExpandButton } from '../shared/settings/settings-card';
import { getCapabilityVisual } from './agent-customize.constants';
import { getCapabilityEnableState, getCapabilitySummary } from './agent-customize.helpers';
import type { CustomizeHandlers } from './agent-customize.type';
import { CapabilityGroupRow } from './capability-group';

interface CapabilityRowProps {
  capability: CapabilityGroup;
  enabledTools: Record<string, boolean>;
  handlers: CustomizeHandlers;
}

/**
 * An enable-mode capability rendered as a row inside a bucket (e.g. Docs within
 * Google). Header shows the capability badge, name, live summary, and a
 * toggle-all switch; expanding reveals its read and write tool groups. The body
 * dims when every tool is off (re-enable via the header toggle).
 *
 * @param {CapabilityRowProps} props - The capability, draft slices, and handlers
 * @returns {JSX.Element} The capability row
 */
export function CapabilityRow({ capability, enabledTools, handlers }: CapabilityRowProps) {
  const [expanded, setExpanded] = useState(false);
  const visual = getCapabilityVisual(capability.id);
  const state = getCapabilityEnableState(capability, enabledTools);
  const capabilityOff = state.onCount === 0;

  return (
    <Row>
      <Header>
        <ExpandButton
          type="button"
          aria-expanded={expanded}
          aria-label={`${capability.name} tools`}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <ExpandChevron expanded={expanded} size={15} color="var(--ink-lighter)" />
          <IconTile background={visual.badgeBg} size="sm">
            <Icon name={visual.icon} size={16} color={visual.color} />
          </IconTile>
          <Info>
            <Text size="regular" weight="medium" color="var(--ink-darkest)">
              {capability.name}
            </Text>
            <Text size="tiny" color="var(--ink-lighter)">
              {getCapabilitySummary(capability, enabledTools)}
            </Text>
          </Info>
        </ExpandButton>
        {state.hasOptional && (
          <Tooltip variant="black" label={`Enable or disable ${capability.name} tools`}>
            <SwitchWrap>
              <Switch
                on={state.allOn}
                ariaLabel={`Enable or disable ${capability.name} tools`}
                onToggle={() => handlers.setTools(state.optionalNames, !state.allOn)}
              />
            </SwitchWrap>
          </Tooltip>
        )}
      </Header>

      {expanded && (
        <Body>
          {capability.read.length > 0 && (
            <CapabilityGroupRow
              kind="read"
              mode={CapabilityMode.Enable}
              tools={capability.read}
              enabledTools={enabledTools}
              parentDisabled={capabilityOff}
            />
          )}
          {capability.write.length > 0 && (
            <CapabilityGroupRow
              kind="write"
              mode={CapabilityMode.Enable}
              tools={capability.write}
              enabledTools={enabledTools}
              parentDisabled={capabilityOff}
            />
          )}
        </Body>
      )}
    </Row>
  );
}

const Row = styled.div`
  border-top: 1px solid var(--sky-light);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 14px 0;
`;

const ExpandButton = styled(SharedExpandButton)`
  gap: 11px;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

const SwitchWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;

const Body = styled.div`
  padding: 0 0 6px 41px;
`;
